import { Badge, Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Space, Spin, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeviceLinking, LinkingDeviceRequest, Terminal } from '@/models/terminal';
import { Key, useEffect, useState } from 'react';
import { TerminalApi } from '@/apis/terminal.aspi';
import Search from 'antd/es/input/Search';
import showNotification from '@/components/common/notification';
import PaginationComponent from '@/components/common/pagination/component/pagination';
import { ColumnsType } from 'antd/es/table';
import "./index.scss";
import SearchComponent from '@/components/common/search-component/search';

interface ModalDeviceLinkingProps {
	visible: boolean;
	onClose:(isUpdated?: boolean) => void; 
	terminal?: Terminal;
}

function ModalDeviceLinking({
	visible,
	onClose,
	terminal,
}: ModalDeviceLinkingProps) {
	const { t } = useTranslation();
	const [deviceLinking, setDeviceLinking] = useState<DeviceLinking[]>([]);
	const [isLoadingModal, setLoadingModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [pageSize, setPageSize] = useState(5);
	const [pageNumber, setPageNumber] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]); // list cua table
	const [selectListSubmit , setSelectListSubmit] = useState<Key[]>([]); // list all 
	const ids = deviceLinking.map(device => device.id);
	const [searchText, setSearchText] = useState("");

	useEffect(() => {
		if (visible) {
			handleGetAllAccessFilter();
		}
	}, [visible, terminal]);

	useEffect(() => {
		if (selectListSubmit) {
			setSelectedRowKeys(selectListSubmit);
		}
	}, [selectListSubmit]);

	const tableScroll = {
		x: 'max-content',
	};
	

	const handleGetAllAccessFilter = async (
		pageSizeData?: number,
		pageNumberData?: number,
		searchString?: string,
	) => {
		pageSizeData = pageSizeData || pageSize;
		pageNumberData = pageNumberData || pageNumber;
		searchString = searchString || '';
		setLoadingModal(true);
		try {
			const { data } = await TerminalApi.getDeviceLinking(
				pageNumberData,
				pageSizeData,
				searchString,
				terminal?.id ?? "",
			);

			const deviceLinkings = data.records.map(
				(resource: DeviceLinking) => ({
					...resource,
				})
			);

			if(selectListSubmit.length <= 0){
				setSelectListSubmit(data.selectedDeviceIds);
			}
			setSelectedRowKeys(selectListSubmit);
			setDeviceLinking(deviceLinkings);
			setTotalRecords(data.totalRecords);
		} catch (error) {

		}
		finally {
			setLoadingModal(false);
			setSearchText(searchString);
		}
	};

	const onSearch = async (value: string) => {
		setLoadingModal(true);
		try {
			await handleGetAllAccessFilter(pageSize, pageNumber, value);
		} catch (error) {
			showNotification('error', "abc");
		} finally {
			setLoadingModal(false);
		}
	}

	const handleCancel = () => {
		onClose();
	};

	const handleCancelUpdated = () => {
		onClose(true);
	};

	const callApiWithSelectedKeys = async () => {
		const selectedRowKeysAsStrings: string[] = selectListSubmit.map((key) =>
		key.toString()
		);

		setIsLoading(true);
		const model: LinkingDeviceRequest = {
			selectedDeviceIds: selectedRowKeysAsStrings,
			sourceDeviceId: terminal?.id ?? "",
		};

		try {
			const { data } = await TerminalApi.linkingDevice(model);
		if (data) {
			showNotification("success", "Update Device Linking successfully.");
			handleCancelUpdated();
		}
		} catch (error) {
		} finally {
			setIsLoading(false);
		}
  	};

	const rowSelection = {
		selectedRowKeys,
		onChange: (newSelectedRowKeys: Key[], selectedRows: DeviceLinking[]) => {
			setSelectedRowKeys(newSelectedRowKeys);
	
			setSelectListSubmit((prevSelectListSubmit) => {
				const keysToAdd = newSelectedRowKeys.filter(
					(key) => !prevSelectListSubmit.includes(key)
				);
	
				const keysToRemove = prevSelectListSubmit.filter(
					(key) => !newSelectedRowKeys.includes(key) && ids.includes(key.toString())
				);
	
				return [...prevSelectListSubmit, ...keysToAdd].filter(
					(key) => !keysToRemove.includes(key)
				);
			});
		},
	};



	const handleOk = () => {
		callApiWithSelectedKeys();
	};

	const columns: ColumnsType<DeviceLinking> = [
		{
			title: "Device Name",
			key: '1',
			render: (text: string, record: any) => <>{record.lszTerminalName}</>,
		},
		{
			title: "Serial Number",
			key: '1',
			render: (text: string, record: any) => <>{record.terminalSerialNumber}</>,
		},
		{
			title: "Brand",
			key: '2',
			render: (text: string, record: any) => <>{record.brand}</>,
		},
		{
			title: "Model",
			key: '3',
			render: (text: string, record: any) => <>{record.model}</>,
		},
		{
			title: "Status",
			key: '6',
			className: 'col-status',
			dataIndex: 'isDeactivate',
			width: 100,
			ellipsis: true,
			render: (isDeactivate) => (
				<Badge status={isDeactivate ? "error" : "success"} text={isDeactivate ? "Deactivate" : "Activate"} />
			),
		},
	];

	function DeviceLinkingTable() {
		return (
			<>
				<div className="ctn-user-index">
					<Row>
						<Col
							className="table-info"
							span={12}>
						</Col>
						<Col
							span={12}
							className="table-cmp">
							<SearchComponent
								className="search-device-linking"
								placeholder="Search"
								handleSearch={onSearch}
								searchText={searchText}
							/>
						</Col>
						<Col span={24}>
							<Row>
								<Col span={24}>
									<Table
										rowSelection={rowSelection}
										loading={isLoadingModal}
										className="tb-list-user"
										columns={columns}
										dataSource={deviceLinking}
										scroll={tableScroll}
										pagination={{
											pageSize: pageSize,
											current: pageNumber,
											style: { display: 'none' },
										}}
										rowKey="id"
									/>
								</Col>
								<PaginationComponent
									totalRecords={totalRecords}
									pageSize={pageSize}
									setPageSize={setPageSize}
									pageNumber={pageNumber}
									setPageNumber={setPageNumber}
									handleGetFilter={handleGetAllAccessFilter}
								/>
							</Row>
						</Col>
					</Row>
				</div>
			</>
		);
	}

	return (
		<>
			<Modal
				className="modal-setup device-linking"
				style={{ top: 170 }}
				width={800}
				title={"Add Device Linking"}
				open={visible}
				onCancel={handleCancel}
				okButtonProps={{ loading: isLoading }}
				onOk={handleOk}
				maskClosable={false}
			>
				<DeviceLinkingTable />
			</Modal>
		</>
	);
}

export default ModalDeviceLinking;
